import React, { useState, useEffect } from "react";
import { useBLE } from "../../../hooks/useBle"; // Adjust path as needed
import { SHOW_THERAPY_CONTROLS } from "../../../utils/constants";

interface Ripple {
  id: number;
  x: number;
  y: number;
}

interface LongPressButtonProps {
  iconType: "plus" | "minus";
  ariaLabel: string;
  color?: string;
  size?: number;
  delay?: number;
  isShortHeight?: boolean;
  onTouchStart: () => void;
  onTouchEnd: () => void;
  containerHeight?: number; // New prop to help with responsive sizing
}

const LongPressButton: React.FC<LongPressButtonProps> = ({
  onTouchStart,
  onTouchEnd,
  iconType,
  ariaLabel,
  containerHeight,
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const [scale, setScale] = useState(1);
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const { isConnected, therapyState } = useBLE();
  // Needs a connected device, running therapy, and a native build.
  const isEnabled = isConnected && therapyState && SHOW_THERAPY_CONTROLS;
  // Dynamic sizing based on screen height and container
  const getResponsiveSizes = () => {
    // Guard for SSR: ensure window is available before using window-based measurements below
    if (typeof window === "undefined") {
      return {
        buttonSize: 140,
        iconThickness: 6,
        iconLength: 70,
        borderRadius: 24,
        spacing: 20,
      };
    }

    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;
    const availableHeight = containerHeight || screenHeight * 0.4; // Fallback to 40% of screen
    const metricHeight = Math.min(screenHeight, availableHeight);

    // Calculate button size as percentage of metricHeight (unified metric for all breakpoints)
    const baseButtonSize = Math.min(metricHeight * 0.3, 160); // Tighter cap for small devices
    let buttonSize;
    let iconThickness;
    let iconLength;
    let borderRadius;
    let spacing;

    if (metricHeight <= 667) {
      // iPhone SE, small phones
      buttonSize = Math.max(baseButtonSize * 0.85, 100);
      iconThickness = 4;
      iconLength = buttonSize * 0.4;
      borderRadius = 20;
      spacing = 12;
    } else if (metricHeight <= 736) {
      // iPhone 8 Plus
      buttonSize = Math.max(baseButtonSize * 0.9, 120);
      iconThickness = 5;
      iconLength = buttonSize * 0.45;
      borderRadius = 22;
      spacing = 16;
    } else if (metricHeight <= 812) {
      // iPhone X/11 Pro
      buttonSize = Math.max(baseButtonSize, 140);
      iconThickness = 6;
      iconLength = buttonSize * 0.5;
      borderRadius = 24;
      spacing = 20;
    } else if (metricHeight <= 926) {
      // iPhone 12/13/14 Pro Max
      buttonSize = Math.max(baseButtonSize * 1.1, 160);
      iconThickness = 7;
      iconLength = buttonSize * 0.52;
      borderRadius = 26;
      spacing = 24;
    } else {
      // Very large screens
      buttonSize = Math.max(baseButtonSize * 1.2, 180);
      iconThickness = 8;
      iconLength = buttonSize * 0.55;
      borderRadius = 28;
      spacing = 28;
    }

    // On small screens, scale down the control for better fit
    if (screenWidth <= 400 || screenHeight <= 640) {
      const smallScale = screenWidth <= 360 || screenHeight <= 600 ? 0.8 : 0.9;
      buttonSize = Math.round(buttonSize * smallScale);
      iconThickness = Math.max(3, Math.round(iconThickness * smallScale));
      iconLength = Math.round(iconLength * smallScale);
      borderRadius = Math.round(borderRadius * smallScale);
      spacing = Math.round(spacing * smallScale);
    }

    // Tablet override: containerHeight is artificially capped too low for tablets,
    // so enforce minimum sizes based on screenWidth instead of scaling down.
    if (screenWidth >= 768) {
      buttonSize = Math.max(buttonSize, 150);
      iconThickness = Math.max(iconThickness, 7);
      iconLength = Math.round(buttonSize * 0.5);
      borderRadius = 26;
      spacing = 24;
    }

    return {
      buttonSize: Math.round(buttonSize),
      iconThickness: Math.round(iconThickness),
      iconLength: Math.round(iconLength),
      borderRadius: Math.round(borderRadius),
      spacing,
    };
  };

  const sizes = getResponsiveSizes();

  // Haptic feedback function
  const triggerHapticFeedback = () => {
    if ("vibrate" in navigator) {
      navigator.vibrate(50); // Short vibration
    }
  };

  const handlePressStart = () => {
    if (!isEnabled) {
      // Provide haptic feedback for disabled state
      triggerHapticFeedback();
      return;
    }

    setIsPressed(true);
    setScale(0.95);
    onTouchStart();
  };

  const handlePressEnd = () => {
    if (!isEnabled) {
      return;
    }

    setIsPressed(false);
    setScale(1);
    onTouchEnd();
  };

  useEffect(() => {
    const cleanup = setTimeout(() => {
      if (ripples.length > 0) {
        setRipples((prev) => prev.slice(1));
      }
    }, 800);
    return () => clearTimeout(cleanup);
  }, [ripples]);

  // Color scheme based on connection and therapy state
  const getColors = () => {
    if (!SHOW_THERAPY_CONTROLS) {
      // Web: muted but still legible on black.
      return {
        // Ghosted: glyph close to fill so it barely reads.
        backgroundColor: "#1E1E20",
        iconColor: "#3A3A3E",
        shadowOpacity: 0,
        opacity: 0.3,
      };
    }
    if (isConnected && therapyState) {
      // Connected + Therapy Running = Enabled (bright)
      return {
        backgroundColor: "#2C2C2E",
        iconColor: "#CCCCCC",
        shadowOpacity: 1,
        opacity: 1,
      };
    } else if (isConnected && !therapyState) {
      // Connected + Therapy NOT Running = Disabled but ready (medium dim)
      return {
        backgroundColor: "#1E1E20",
        iconColor: "#666666",
        shadowOpacity: 0.5,
        opacity: 0.7,
      };
    } else {
      // Disconnected = Fully disabled (very dim)
      return {
        backgroundColor: "#1A1A1C",
        iconColor: "#4A4A4C",
        shadowOpacity: 0.2,
        opacity: 0.5,
      };
    }
  };

  const colors = getColors();

  return (
    <button
      aria-label={ariaLabel}
      className="relative overflow-hidden focus:outline-none active:outline-none"
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressEnd}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      disabled={!isEnabled}
      style={{
        width: `${sizes.buttonSize}px`,
        height: `${sizes.buttonSize}px`,
        transform: `scale(${scale})`,
        transition: "all 0.15s ease-out",
        borderRadius: `${sizes.borderRadius}px`,
        backgroundColor: colors.backgroundColor,
        marginLeft: `${sizes.spacing / 2}px`,
        marginRight: `${sizes.spacing / 2}px`,
        opacity: colors.opacity,
        cursor: isEnabled ? "pointer" : "not-allowed",
        // Enhanced shadow for depth, dimmed when disabled
        boxShadow: `
          0 8px 16px rgba(0, 0, 0, ${0.3 * colors.shadowOpacity}),
          0 4px 8px rgba(0, 0, 0, ${0.2 * colors.shadowOpacity}),
          inset 0 1px 0 rgba(255, 255, 255, ${0.1 * colors.shadowOpacity})
        `,
        border: "none",
      }}
    >
      {/* Icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Horizontal line for both + and - */}
        <div
          style={{
            width: `${sizes.iconLength}px`,
            height: `${sizes.iconThickness}px`,
            backgroundColor: colors.iconColor,
            borderRadius: `${sizes.iconThickness / 2}px`,
            transition: "background-color 0.2s ease",
          }}
        />
        {/* Vertical line only for + */}
        {iconType === "plus" && (
          <div
            className="absolute"
            style={{
              width: `${sizes.iconThickness}px`,
              height: `${sizes.iconLength}px`,
              backgroundColor: colors.iconColor,
              borderRadius: `${sizes.iconThickness / 2}px`,
              transition: "background-color 0.2s ease",
            }}
          />
        )}
      </div>

      {/* Ripple Effect - only when enabled */}
      {isEnabled &&
        ripples.map((ripple) => (
          <span
            key={ripple.id}
            className="absolute rounded-full bg-white"
            style={{
              left: ripple.x - 25,
              top: ripple.y - 25,
              width: 50,
              height: 50,
              transform: "scale(0)",
              animation: "ripple 0.8s ease-out",
              opacity: 0.2,
            }}
          />
        ))}

      {/* Press state overlay - only when enabled */}
      {isPressed && isEnabled && (
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: `${sizes.borderRadius}px`,
          }}
        />
      )}

      {/* CSS Styles */}
      <style>{`
        @keyframes ripple {
          to {
            transform: scale(2.5);
            opacity: 0;
          }
        }
      `}</style>
    </button>
  );
};

export default LongPressButton;
